"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { User, Lock } from "lucide-react";
function LoginFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  if (isSubmitted && !submitError) {
    return <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,25%)] relative">
        <div className="absolute inset-0 bg-[hsl(230,80%,45%)]/5 pointer-events-none"></div>
        <Card ref={ref} className="w-full max-w-md mx-4 bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,93%)] rounded-lg shadow-soft transition-all duration-[150ms] ease-[cubic-bezier(0.22,1,0.36,1)]" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 150ms ease-out, transform 150ms ease-out'
      }}>
          <CardContent className="pt-6">
            <div className="p-6 bg-[hsl(230,80%,95%)] border border-[hsl(230,80%,90%)] rounded-lg">
              <p className="text-[hsl(230,80%,45%)] font-semibold">{successMessage}</p>
              <Button onClick={resetForm} variant="outline" className="mt-4 w-full">
                Login Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,25%)] relative">
      <div className="absolute inset-0 bg-[hsl(230,80%,45%)]/5 pointer-events-none"></div>
      <Card ref={ref} className="w-full max-w-md mx-4 bg-[hsl(0,0%,100%)] border border-[hsl(0,0%,93%)] rounded-lg shadow-soft transition-all duration-[150ms] ease-[cubic-bezier(0.22,1,0.36,1)]" style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 150ms ease-out, transform 150ms ease-out'
    }}>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-[hsl(0,0%,12%)]">Welcome Back</CardTitle>
          <CardDescription className="text-[hsl(0,0%,40%)]">Enter your credentials to login</CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit}>
            <FormField control={control} name="username" rules={{
            required: "Username is required",
            minLength: {
              value: 3,
              message: "Username must be at least 3 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[hsl(0,0%,12%)] font-medium">Username</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(0,0%,60%)] size-4" />
                      <Input placeholder="Enter your username" {...field} className="pl-10 transition-all duration-[100ms] ease-out focus:border-[hsl(230,80%,45%)] focus:ring-[hsl(230,80%,45%)]/50" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="password" rules={{
            required: "Password is required",
            minLength: {
              value: 6,
              message: "Password must be at least 6 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[hsl(0,0%,12%)] font-medium">Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(0,0%,60%)] size-4" />
                      <Input type="password" placeholder="Enter your password" {...field} className="pl-10 transition-all duration-[100ms] ease-out focus:border-[hsl(230,80%,45%)] focus:ring-[hsl(230,80%,45%)]/50" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-4 bg-[hsl(0,70%,95%)] border border-[hsl(0,70%,85%)] rounded-lg">
                <p className="text-[hsl(0,70%,40%)] text-sm">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full bg-[hsl(230,80%,45%)] text-[hsl(0,0%,100%)] hover:bg-[hsl(230,85%,40%)] hover:-translate-y-0.5 active:bg-[hsl(230,90%,35%)] active:translate-y-0 transition-all duration-[80ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] shadow-soft hover:shadow-medium">
              {isSubmitting ? <>
                  <span className="animate-spin mr-2">⏳</span>
                  Logging in...
                </> : 'Login'}
            </Button>
          </Form>
        </CardContent>
      </Card>
    </div>;
}
export default function LoginForm() {
  return <FormProvider className="min-h-screen" formId="login-form-main" sectionName="login-section" defaultValues={{
    username: "",
    password: ""
  }} validationRules={{
    username: {
      required: true,
      minLength: 3
    },
    password: {
      required: true,
      minLength: 6
    }
  }} submitText="Login" successMessage="Login successful! Redirecting..." errorMessage="Invalid credentials. Please try again.">
      <LoginFormContent />
    </FormProvider>;
}